'use client';

import PageLayout from '@/app/components/common/PageLayout';
import Header from '@/app/components/common/Header';
import Icon from '@/app/components/ui/AppIcon';
import { motion } from 'framer-motion';
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    category: 'General',
    question: 'Do I need to book an appointment before coming?',
    answer: 'Yes, appointments are required so we can reserve enough time for your service. Booking ahead helps us prepare for your hair type, selected style, and treatment needs.'
  },
  {
    category: 'General',
    question: 'What should I bring to my first appointment?',
    answer: 'Come with your hair in its current state and share any relevant hair history (recent relaxers, color, heat damage, or scalp concerns). You can also bring inspiration photos so your stylist can guide you properly.'
  },
  {
    category: 'General',
    question: 'Do you work on all hair textures?',
    answer: 'Yes. We provide care for natural hair across different textures and densities, and we tailor each service to your hair condition, styling goals, and maintenance routine.'
  },
  {
    category: 'Services',
    question: 'What is the difference between a regular wash and a scalp exfoliating wash?',
    answer: 'A regular wash refreshes your hair and scalp for routine maintenance. A scalp exfoliating wash is a deeper cleanse designed to remove buildup and improve scalp health, especially if you have itchiness, flakes, or heavy product buildup.'
  },
  {
    category: 'Services',
    question: 'How long do twist services usually take?',
    answer: 'Timing depends on the style, hair length, and fullness. Mini twists and long/full styles generally take longer than short or restyle options. Your stylist will confirm an estimated duration during booking or consultation.'
  },
  {
    category: 'Services',
    question: 'Can I book add-on services with my main appointment?',
    answer: 'Yes. Add-ons like scalp exfoliation, moisturizing, and detangling can be included with your main service. This is recommended if your hair or scalp needs extra prep before styling.'
  },
  {
    category: 'Services',
    question: 'Do you offer services for children?',
    answer: 'Yes. We provide kid-friendly options such as school hairstyles and age-appropriate natural styles. Our team keeps the experience gentle, neat, and comfortable for children.'
  },
  {
    category: 'Booking',
    question: 'What happens if I arrive late?',
    answer: 'Please arrive on time to avoid disrupting scheduled appointments. Late arrival may attract an extra fee, shorten your service, or lead to cancellation depending on how late you are.'
  },
  {
    category: 'Booking',
    question: 'What is your cancellation policy?',
    answer: 'We recommend cancelling or rescheduling early so your slot can be reassigned. Last-minute cancellations and no-shows may attract a fee based on the time already reserved for your appointment.'
  },
  {
    category: 'Hair Care',
    question: 'My hair is very tangled. Should I add detangling before styling?',
    answer: 'Yes, especially if your hair has knots or is matted. Adding detangling helps protect your strands, reduces breakage, and allows your stylist to deliver a cleaner, longer-lasting finish.'
  },
  {
    category: 'Hair Care',
    question: 'How do I make my style last longer after my appointment?',
    answer: 'Protect your hair at night with a satin bonnet or scarf, avoid heavy product buildup, keep your scalp clean, and follow aftercare guidance from your stylist for moisture and maintenance.'
  },
  {
    category: 'Pricing',
    question: 'Why do prices vary for fuller or longer hair?',
    answer: 'Very full or long hair requires more time, sectioning, product, and technical effort. In such cases, a higher charge may apply and will be communicated clearly before or during your appointment.'
  },
  {
    category: 'Pricing',
    question: 'How can I confirm the final cost of my service?',
    answer: 'Service prices are listed as a guide. Final cost depends on selected style, hair condition, length, and any add-ons. You can request a quick consultation for a clearer estimate.'
  },
  {
    category: 'Payment',
    question: 'What payment methods do you accept?',
    answer: 'We accept major local payment options available at the salon, including transfer and card where applicable. Confirm available methods when booking if needed.'
  },
  {
    category: 'Payment',
    question: 'Do I need to pay a deposit to secure my slot?',
    answer: 'Some appointments may require a booking fee or deposit, especially for time-intensive services. This helps secure your slot and reduce last-minute cancellations.'
  }
];

const categories = ['All', 'General', 'Services', 'Booking', 'Hair Care', 'Pricing', 'Payment'];

export default function FAQPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filteredFAQs = selectedCategory === 'All'
    ? faqData
    : faqData.filter(faq => faq.category === selectedCategory);

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-primary text-primary-foreground">
        <Header />
        <div className="container mx-auto px-6 py-20 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl opacity-90">
              Find answers to common questions about our hair services, booking, pricing, and aftercare
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-secondary border-b border-border">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  setOpenIndex(0);
                }}
                className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground shadow-lg'
                    : 'bg-card text-foreground hover:bg-muted border border-border'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {filteredFAQs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="bg-card rounded-3xl shadow-lg border border-border overflow-hidden"
                >
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full p-6 text-left flex items-center justify-between gap-4 hover:bg-secondary/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xs font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
                          {faq.category}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-foreground">{faq.question}</h3>
                    </div>
                    <Icon
                      name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={24}
                      className="text-primary flex-shrink-0"
                    />
                  </button>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6">
                        <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Still Have Questions?</h2>
            <p className="text-lg mb-8 opacity-90">
              Our friendly team is here to help. Contact us today for personalized answers to your questions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-xl font-semibold shadow-lg transition-all"
            >
              Contact Us
            </motion.button>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
}

